import { Map as MapRef } from '@cian/frontend-newbuilding-map-component';
import { IGeoObject } from '@cian/frontend-newbuilding-map-component/es/types';
import { IControl } from '@cian/frontend-newbuilding-map-component/es/types/control';
import { IGeoObjectsFetchData } from '@cian/frontend-newbuilding-map-component/es/types/fetch';
import { useIsDesktop } from '@cian/valuation-utils-component';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IGetInfrastructureV4Response } from 'shared/common/repositories/infrastructure-caching/entities/get_infrastructure/GetInfrastructureV4Response';
import { IInfrastructureProps } from 'shared/mf-infrastructure/containers/InfrastructureContainer/types';
import { useWidgetSettingsContext } from 'shared/mf-infrastructure/containers/WidgetSettingsContext';
import { useAppDispatch } from 'shared/mf-infrastructure/hooks';
import { transformSimilarObjectsToGeoObjects } from 'shared/mf-infrastructure/mappers/transformGetAjaxMapRoundaboutResponse';
import { transformResultToGeoObjects } from 'shared/mf-infrastructure/mappers/transformGetInfrastructureV3Response/transformResultToGeoObjects';
import { transformSimilarNewbuildingsToGeoObjects } from 'shared/mf-infrastructure/mappers/transformGetRecommendationsNearNewbuildingResponse';
import { transformTransportAccessibilityInfrastructure } from 'shared/mf-infrastructure/mappers/transformGetInfrastructureV3Response/transformTransportAccessibilityInfrastructure';
import {
  selectAvailableInfrastructureTypes,
  selectInfrastructureApiResult,
  selectInfrastructureStatus,
} from 'shared/mf-infrastructure/selectors/infrastructure';
import { selectActiveInfrastructureTypes } from 'shared/mf-infrastructure/selectors/infrastructure/selectActiveInfrastructureTypes';
import { selectInfrastructureCenterCoordinates } from 'shared/mf-infrastructure/selectors/infrastructure/selectInfrastructureCenterCoordinates';
import { selectInfrastructureGeoObjectsByYearMap } from 'shared/mf-infrastructure/selectors/infrastructure/selectInfrastructureGeoObjectsByYearMap';
import { selectSimilarNewbuildingsApiResult } from 'shared/mf-infrastructure/selectors/similarNewbuildings';
import { infrastructureActions } from 'shared/mf-infrastructure/slices';
import { loadInfrastructure, loadSimilarNewbuildings, loadSimilarObjects } from 'shared/mf-infrastructure/thunks';
import { ISetInfrastrutureTypesPayload } from 'shared/mf-infrastructure/types/infrastructure';
import { ITAInfrastructureData, TTAInfrastructureType } from 'shared/mf-infrastructure/types/transportAccessibility';
import { ITransportAccessibilityRate } from 'shared/mf-infrastructure/types/transportAccessibilityTypes';
import { getTransportAccessibilityInfrastructureMapByType } from 'shared/mf-infrastructure/utils/getTransportAccessibilityInfrastructureByType';

function normalizeTransportAccessibilityRate(
  rate: IGetInfrastructureV4Response['transportAccessibilityRating'],
): ITransportAccessibilityRate | null {
  if (!rate) {
    return null;
  }

  return {
    ...rate,
    infrastructure: rate.infrastructure || undefined,
    transport: rate.transport || undefined,
  };
}

export const useInfrastructureProps = (): IInfrastructureProps => {
  const { profile, yandexApiKey, yandexSuggestApiKey, showOfferMarker } = useWidgetSettingsContext();
  const isDesktop = useIsDesktop();
  const dispatch = useAppDispatch();

  const infrastructureStatus = useSelector(selectInfrastructureStatus);
  const infrastructureByYearMap = useSelector(selectInfrastructureGeoObjectsByYearMap);
  const centeredCoordinates = useSelector(selectInfrastructureCenterCoordinates);
  const infrastructureTypes = useSelector(selectAvailableInfrastructureTypes);
  const currentTypes = useSelector(selectActiveInfrastructureTypes);
  const infrastructureData = useSelector(selectInfrastructureApiResult);
  const similarNewbuildingsData = useSelector(selectSimilarNewbuildingsApiResult);

  const mapRef = useRef<MapRef>(null);

  /**
   * Данные транспортной доступности зависят только от ответа API, поэтому считаются из стора,
   * а не заполняются после загрузки карты: иначе сайдбар, открытый до карты, остаётся пустым.
   */
  const transportAccessibilityInfrastructureByType = useMemo(
    () =>
      infrastructureData
        ? getTransportAccessibilityInfrastructureMapByType(infrastructureData, showOfferMarker)
        : new Map<TTAInfrastructureType, ITAInfrastructureData>(),
    [infrastructureData, showOfferMarker],
  );
  const transportAccessibilityInfrastructure = useMemo(
    () => transformTransportAccessibilityInfrastructure(infrastructureData?.transportAccessibility),
    [infrastructureData],
  );

  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const suggestPosition = isDesktop ? 'bottom' : 'top';

  let controls: IControl[] = [
    {
      type: 'ZoomControl',
      onClick: () => {},
    },
  ];

  if (isDesktop) {
    controls = [
      ...controls,
      { type: 'PanoramaControl' },
      { type: 'RulerControl' },
      { type: 'FullscreenControl' },
      { type: 'GeolocationControl' },
    ];
  }

  const setInfrastructureType = useCallback(
    (payload: ISetInfrastrutureTypesPayload) => {
      dispatch(infrastructureActions.setTypes(payload));
    },
    [dispatch],
  );

  const handleMapLoaded = useCallback(() => setIsMapLoaded(true), []);

  const handleFetchSimilarObjects = useCallback(
    async ({ bbox }: IGeoObjectsFetchData): Promise<IGeoObject[]> => {
      try {
        const result = await dispatch(loadSimilarObjects(bbox)).unwrap();

        return transformSimilarObjectsToGeoObjects(result);
      } catch (error) {
        return [];
      }
    },
    [dispatch],
  );

  const handleFetchSimilarNewbuildings = useCallback(async (): Promise<IGeoObject[]> => {
    try {
      const result = similarNewbuildingsData
        ? similarNewbuildingsData
        : await dispatch(loadSimilarNewbuildings()).unwrap();

      if (result) {
        return transformSimilarNewbuildingsToGeoObjects(result);
      }

      return [];
    } catch (error) {
      return [];
    }
  }, [dispatch, similarNewbuildingsData]);

  const handleFetchInfrastructure = useCallback(
    async (fetchData: IGeoObjectsFetchData): Promise<IGeoObject[]> => {
      let data: IGetInfrastructureV4Response | null = infrastructureData;

      if (!data) {
        try {
          data = await dispatch(loadInfrastructure(profile)).unwrap();
        } catch (e) {
          // nomnom
        }
      }

      if (data === null) {
        return [];
      }

      const similarObjects = await handleFetchSimilarObjects(fetchData);
      const similarNewbuildings = await handleFetchSimilarNewbuildings();

      return [...transformResultToGeoObjects(data, showOfferMarker), ...similarObjects, ...similarNewbuildings];
    },
    [
      infrastructureData,
      handleFetchSimilarNewbuildings,
      handleFetchSimilarObjects,
      centeredCoordinates,
      dispatch,
      profile,
      showOfferMarker,
    ],
  );

  return {
    apiKey: yandexApiKey,
    suggestApiKey: yandexSuggestApiKey,
    mapRef,
    isMapLoaded,
    centeredCoordinates,
    currentTypes,
    infrastructureByYearMap,
    infrastructureTypes,
    setInfrastructureType,
    handleMapLoaded,
    controls,
    suggestPosition,
    showOfferMarker,
    onGeoObjectsFetch: handleFetchInfrastructure,
    isInfrastructureFetched: infrastructureStatus === 'fulfilled',
    transportAccessibilityInfrastructureByType,
    transportAccessibilityInfrastructure,
    transportAccessibilityRate: normalizeTransportAccessibilityRate(infrastructureData?.transportAccessibilityRating),
  };
};
